import { inject } from '@angular/core';
import { IsActiveMatchOptions, Navigation, Router } from '@angular/router';

import { Logger, NavigationInfo } from '@frontend/vanilla/core';

import { VIEW_TRANSITION_LOG_TAG as LOG_TAG } from './view-transition.constants';

// Match routes if segments are the same
const urlMatchingOptions: IsActiveMatchOptions = {
    paths: 'exact',
    matrixParams: 'exact',
    fragment: 'ignored',
    queryParams: 'ignored',
};

/** Indicates whether the current navigation is to the active page */
export const isNavigationToCurrentPage = (): boolean => {
    const logger = inject(Logger);
    const router = inject(Router);

    const incomingUrl = router.getCurrentNavigation()?.finalUrl;

    if (!incomingUrl) {
        logger.error(`${LOG_TAG} isNavigationToCurrentPage called outside the context of an active navigation`);

        return false;
    }

    const _isNavigationToCurrentPage = router.isActive(incomingUrl, urlMatchingOptions);

    if (_isNavigationToCurrentPage) {
        logger.debug(`${LOG_TAG} Detected navigation to the current page`);
    }

    return _isNavigationToCurrentPage;
};

/** Indicates whether the current navigation is flagged to skip the view transition */
export const isNavigationFlaggedSkipTransition = (): boolean => {
    const currentNavigation = inject(Router).getCurrentNavigation();

    if (!currentNavigation) {
        inject(Logger).error(`${LOG_TAG} isNavigationFlaggedSkipTransition called outside the context of an active navigation`);

        return false;
    }

    return Boolean((currentNavigation?.extras?.info as NavigationInfo)?.skipTransition);
};

/** Indicates whether the current navigation is an in-app 'back' navigation (e.g. in-app back or breadcrumbs button click) */
export const isNavigationApplicationBack = (): boolean => {
    if (isNavigationTraversal()) {
        return false;
    }

    const currentNavigation = inject(Router).getCurrentNavigation();

    if (!currentNavigation) {
        inject(Logger).error(`${LOG_TAG} isNavigationApplicationBack called outside the context of an active navigation`);

        return false;
    }

    return isNavigationFlaggedBack(currentNavigation) || isNavigationToAncestorRoute(currentNavigation);
};

/** Indicates whether the current navigation is a traversal through existing history (i.e. browser back or forward navigations) */
export const isNavigationTraversal = (): boolean => {
    return inject(Router).getCurrentNavigation()?.trigger === 'popstate';
};

const isNavigationFlaggedBack = (currentNavigation: Navigation): boolean => {
    const isNavigationFlaggedBack = Boolean((currentNavigation?.extras?.info as NavigationInfo)?.isBackNavigation);

    if (isNavigationFlaggedBack) {
        inject(Logger).debug(`${LOG_TAG} The current navigation is flagged as an in-app back navigation`);
    }

    return isNavigationFlaggedBack;
};

const isNavigationToAncestorRoute = (currentNavigation: Navigation): boolean => {
    const currentUrl = currentNavigation.finalUrl?.toString();
    const previousUrl = currentNavigation.previousNavigation?.finalUrl?.toString();
    const isAncestorRoute = currentUrl !== undefined && previousUrl?.includes(currentUrl);

    if (isAncestorRoute) {
        inject(Logger).debug(`${LOG_TAG} The current navigation is to an ancestor of the previous URL`);
    }

    return Boolean(isAncestorRoute);
};
